import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../../services/adminService';

interface SlackConfig {
  workspace?: string;
  channel?: string;
  enabled?: boolean;
  event_filters?: string[];
}

const ALL_EVENTS = ['approvals', 'payouts', 'webhook_failures'];

const SlackIntegration: React.FC = () => {
  const [config, setConfig] = useState<SlackConfig>({ enabled: false, event_filters: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await adminService.getSlackConfig();
        if (mounted) setConfig({ enabled: !!data?.enabled, workspace: data?.workspace || '', channel: data?.channel || '', event_filters: data?.event_filters || [] });
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load Slack configuration');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    if (!config.workspace || !config.channel) {
      toast.error('Workspace and Channel are required');
      return;
    }
    setSaving(true);
    try {
      await adminService.saveSlackConfig(config);
      toast.success('Slack configuration saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save Slack configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setSending(true);
    try {
      const res = await adminService.sendSlackTest();
      if (res?.delivered) toast.success('Test message delivered'); else toast('Test sent');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send test message');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading Slack settings…</div>;

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Slack Notifications</h3>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!config.enabled} onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))} />
          <span>Enabled</span>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1" htmlFor="slack-workspace">Workspace</label>
          <input id="slack-workspace" className="border rounded px-3 py-2 w-full" value={config.workspace || ''} onChange={(e) => setConfig((c) => ({ ...c, workspace: e.target.value }))} placeholder="acme" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1" htmlFor="slack-channel">Channel</label>
          <input id="slack-channel" className="border rounded px-3 py-2 w-full" value={config.channel || ''} onChange={(e) => setConfig((c) => ({ ...c, channel: e.target.value }))} placeholder="#affiliate-notifs" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Events</label>
          <div className="flex flex-wrap gap-3">
            {ALL_EVENTS.map((ev) => (
              <label key={ev} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!config.event_filters?.includes(ev)}
                  onChange={(e) => {
                    setConfig((c) => {
                      const set = new Set(c.event_filters || []);
                      if (e.target.checked) set.add(ev); else set.delete(ev);
                      return { ...c, event_filters: Array.from(set) };
                    });
                  }}
                />
                <span>{ev}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={handleTest} disabled={sending} className="px-3 py-2 rounded border disabled:opacity-60">{sending ? 'Sending…' : 'Send Test'}</button>
      </div>
    </div>
  );
};

export default SlackIntegration;
